import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, map } from 'rxjs';
import { environment } from '../../environments/environment';

const STORAGE_KEY_TOKEN = 'business_auth_token';
const STORAGE_KEY_ACCOUNT = 'business_account';

/**
 * `localStorage` se comparte entre pestañas; `sessionStorage` no. Sin esto, Cmd+clic
 * en enlaces de empresas abre una pestaña sin token y el guard manda al login.
 */
function readBusinessStorage(key: string): string | null {
  const fromLocal = localStorage.getItem(key);
  if (fromLocal) return fromLocal;
  const fromSession = sessionStorage.getItem(key);
  if (fromSession) {
    localStorage.setItem(key, fromSession);
    sessionStorage.removeItem(key);
    return fromSession;
  }
  return null;
}

function writeBusinessStorage(key: string, value: string): void {
  localStorage.setItem(key, value);
  sessionStorage.removeItem(key);
}

function clearBusinessStorageKey(key: string): void {
  localStorage.removeItem(key);
  sessionStorage.removeItem(key);
}

export interface BusinessAccount {
  id: string;
  isCompanyAdmin?: boolean;
  canGenerateCompanyCodes?: boolean;
  requireLinkedPassengers?: boolean;
  requireBeneficiaryEmployeeNumber?: boolean;
  companyRoot?: {
    account?: BusinessAccount | null;
    primaryPassenger?: {
      id?: string | null;
      userId?: string | null;
    } | null;
  } | null;
  primaryPassenger?: {
    id?: string | null;
    userId?: string | null;
  } | null;
  email: string;
  contactName: string;
  contactPhone: string;
  tradeName: string;
  legalName: string;
  industry: string;
  rfc: string;
  fiscalStreet: string;
  fiscalExteriorNumber: string;
  fiscalInteriorNumber: string;
  fiscalColony: string;
  fiscalPostalCode: string;
  fiscalCity: string;
  fiscalState: string;
  taxRegime: string;
  cfdiUsage: string;
  lastLoginAt: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BusinessLoginRequest {
  email: string;
  password: string;
}

export interface BusinessLoginResponse {
  success: boolean;
  message: string;
  access_token: string;
  businessAccount: BusinessAccount;
}

export interface BusinessChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface BusinessForgotPasswordRequest {
  email: string;
}

export interface BusinessValidateResetTokenRequest {
  token: string;
}

export interface BusinessResetPasswordRequest {
  token: string;
  newPassword: string;
  confirmPassword: string;
}

@Injectable({
  providedIn: 'root'
})
export class BusinessAuthService {

  constructor(
    private http: HttpClient,
    private router: Router
  ) {}

  login(credentials: BusinessLoginRequest): Observable<BusinessLoginResponse> {
    const url = `${environment.apiUrl}/auth/business-login`;
    return this.http.post<BusinessLoginResponse>(url, credentials).pipe(
      tap((res) => {
        if (res.access_token) {
          writeBusinessStorage(STORAGE_KEY_TOKEN, res.access_token);
        }
        if (res.businessAccount) {
          writeBusinessStorage(STORAGE_KEY_ACCOUNT, JSON.stringify(res.businessAccount));
        }
      })
    );
  }

  forgotPassword(body: BusinessForgotPasswordRequest): Observable<{ success?: boolean; message?: string }> {
    const url = `${environment.apiUrl}/auth/business/forgot-password`;
    return this.http.post<{ success?: boolean; message?: string }>(url, body);
  }

  validateResetToken(body: BusinessValidateResetTokenRequest): Observable<{ success?: boolean; message?: string }> {
    const url = `${environment.apiUrl}/auth/business/validate-reset-token`;
    return this.http.post<{ success?: boolean; message?: string }>(url, body);
  }

  resetPassword(body: BusinessResetPasswordRequest): Observable<{ success?: boolean; message?: string }> {
    const url = `${environment.apiUrl}/auth/business/reset-password`;
    return this.http.post<{ success?: boolean; message?: string }>(url, body);
  }

  logout(): void {
    clearBusinessStorageKey(STORAGE_KEY_TOKEN);
    clearBusinessStorageKey(STORAGE_KEY_ACCOUNT);
    this.router.navigateByUrl('/empresas/iniciar-sesion', { replaceUrl: true });
  }

  getToken(): string | null {
    return readBusinessStorage(STORAGE_KEY_TOKEN);
  }

  getBusinessAccount(): BusinessAccount | null {
    const raw = readBusinessStorage(STORAGE_KEY_ACCOUNT);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as BusinessAccount;
    } catch {
      return null;
    }
  }

  /** GET /api/v1/business/account - obtiene la cuenta desde el API (requiere token) */
  fetchAccount(): Observable<BusinessAccount> {
    const token = this.getToken();
    const headers = new HttpHeaders({
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    });
    const url = `${environment.apiUrl}/business/account`;
    return this.http.get<BusinessAccount | { data: BusinessAccount }>(url, { headers }).pipe(
      map((res) => ('data' in res && res.data ? res.data : res) as BusinessAccount),
      tap((account) => {
        if (account?.id) {
          writeBusinessStorage(STORAGE_KEY_ACCOUNT, JSON.stringify(account));
        }
      }),
    );
  }

  /** PATCH /api/v1/business/account/password */
  changePassword(body: BusinessChangePasswordRequest): Observable<unknown> {
    const token = this.getToken();
    const headers = new HttpHeaders({
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    });
    const url = `${environment.apiUrl}/business/account/password`;
    return this.http.patch<unknown>(url, body, { headers });
  }

  isLoggedIn(): boolean {
    return !!this.getToken();
  }
}
